import { LayerDefinition, getLayerDefinition } from '../../layers/definitions';
import { MASTER_LAYER_CATALOG } from '../../config';
import { LayerItemConfig } from '../../types';

export const PRODUCT_TO_CATALOG_ID: Record<string, string> = {
  sunrise: 'golden_hour',
  sunrise_forecast: 'golden_hour',
  sunrise_nowcast: 'golden_hour',
  alpenglow: 'alpenglow_forecast',
  alpenglow_nowcast: 'alpenglow_forecast',
  goldenhour: 'alpenglow_forecast',
  darkskies: 'darkskies_bortle',
  aurora: 'aurora_viewline',
  aqi: 'aqi_index',
  smoke: 'smoke_forecast',
  dust: 'dust_forecast',
  precip: 'precip_forecast',
  precip_24h: 'precip_forecast',
  lightning: 'thunderstorm',
  live_weather_radar: 'precip_radar',
  mrms: 'precip_radar',
  live_weather_lightning: 'lightning_live',
  mrms_lightning: 'lightning_live',
  live_weather_satellite: 'satellite_imagery',
  goes: 'satellite_imagery',
  live_weather_qpe: 'precip_accum',
  rain_accum: 'precip_accum',
  live_weather_snow: 'snow_accum',
  lenticular: 'lenticular_clouds',
  winds: 'winds_surface',
  surface_winds: 'winds_surface',
  rtma_winds: 'winds_surface_current',
  live_weather_temp_gradient: 'hrtemp_current',
  live_weather_temps: 'hrtemp_current',
  sun_intensity: 'sun_intensity_forecast',
  streamflow: 'streamflow_gauges',
  water_temp: 'streamflow_gauges',
  drought: 'drought_usdm',
  cpc_outlooks: 'cpc_outlook',
  tropical: 'tropical_cyclones',
  climate: 'climate_data',
  avalanche: 'avalanche_danger',
  turbulence: 'aviation_turbulence',
  solar_lunar: 'solar_lunar_tools'
};

export function resolveCatalogLayerItem(layerKey: string): LayerItemConfig | undefined {
  if (!layerKey || typeof MASTER_LAYER_CATALOG === 'undefined' || !MASTER_LAYER_CATALOG) {
    return undefined;
  }
  const cleanKey = layerKey.trim().toLowerCase();
  if (MASTER_LAYER_CATALOG[layerKey]) return MASTER_LAYER_CATALOG[layerKey];
  if (MASTER_LAYER_CATALOG[cleanKey]) return MASTER_LAYER_CATALOG[cleanKey];

  const mappedId = PRODUCT_TO_CATALOG_ID[cleanKey];
  if (mappedId && MASTER_LAYER_CATALOG[mappedId]) {
    return MASTER_LAYER_CATALOG[mappedId];
  }

  return Object.values(MASTER_LAYER_CATALOG).find(
    (l: any) =>
      String(l.id || '').toLowerCase() === cleanKey ||
      String(l.product || '').toLowerCase() === cleanKey ||
      (l.model && String(l.model).toLowerCase() === cleanKey)
  );
}

export class LayerDescriptionModal {
  private container: HTMLElement;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'gw-layer-desc-modal';
    this.container.className = 'gw-layer-desc-backdrop';
    this.container.style.display = 'none';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public close(): void {
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  public open(layerKeyOrDef: string | LayerDefinition): void {
    const layerKey = typeof layerKeyOrDef === 'string' ? layerKeyOrDef : layerKeyOrDef.id;
    const catItem = resolveCatalogLayerItem(layerKey);

    const def: LayerDefinition | undefined = typeof layerKeyOrDef === 'string'
      ? (getLayerDefinition(layerKeyOrDef) ||
         (catItem?.id ? getLayerDefinition(catItem.id) : undefined) ||
         (catItem?.product ? getLayerDefinition(String(catItem.product)) : undefined) ||
         (catItem?.model ? getLayerDefinition(String(catItem.model)) : undefined))
      : layerKeyOrDef;

    if (!def && !catItem) {
      console.warn(`[LayerDescriptionModal] No definition found for layer: ${layerKeyOrDef}`);
      return;
    }

    this.close();
    this.container.style.display = 'flex';

    // Prioritize custom/dynamic fields from MASTER_LAYER_CATALOG (activity_configs.yaml) over static TS definitions
    const rawNarrative =
      catItem?.longDescription ||
      catItem?.long_description ||
      def?.description ||
      catItem?.description ||
      '';
    const rawScoreDesc =
      catItem?.scoreDescription ||
      catItem?.score_description ||
      def?.scoreDescription ||
      '';
    const layerName = catItem?.label || def?.name || layerKey;
    const layerIcon = catItem?.icon || def?.icon || '🗺️';
    const categoryBadge =
      catItem?.categoryLabel ||
      catItem?.category_label ||
      def?.categoryLabel ||
      (catItem?.category
        ? String(catItem.category).replace(/_/g, ' ').toUpperCase()
        : def?.category
          ? def.category.replace(/_/g, ' ').toUpperCase()
          : 'Weather Product');

    // Format paragraphs from description
    const descParagraphs = rawNarrative
      .split('\n\n')
      .filter((p: string) => p.trim().length > 0)
      .map((p: string) => `<p class="gw-layer-desc-text">${p.trim()}</p>`)
      .join('');

    // Format score description if available
    let scoreSectionHtml = '';
    if (rawScoreDesc) {
      const scoreItems = rawScoreDesc
        .split('\n')
        .filter((line: string) => line.trim().length > 0)
        .map((line: string) => `<div class="gw-layer-desc-score-item">${line.trim()}</div>`)
        .join('');

      scoreSectionHtml = `
        <div class="gw-layer-desc-section">
          <div class="gw-layer-desc-section-title">
            <span>📊</span> Score & Grading Criteria
          </div>
          <div class="gw-layer-desc-score-box">
            ${scoreItems}
          </div>
        </div>
      `;
    }

    this.container.innerHTML = `
      <div class="gw-layer-desc-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-layer-desc-title">
        <div class="gw-layer-desc-header">
          <div class="gw-layer-desc-title-group">
            <span class="gw-layer-desc-icon">${layerIcon}</span>
            <div>
              <h2 id="gw-layer-desc-title" class="gw-layer-desc-title">${layerName}</h2>
              <span class="gw-layer-desc-category">${categoryBadge}</span>
            </div>
          </div>
          <button id="gw-layer-desc-close-btn" class="gw-layer-desc-close" title="Close layer description" aria-label="Close">&times;</button>
        </div>

        <div class="gw-layer-desc-body">
          <div class="gw-layer-desc-section">
            <div class="gw-layer-desc-section-title">
              <span>ℹ️</span> Description
            </div>
            <div class="gw-layer-desc-narrative">
              ${descParagraphs}
            </div>
          </div>

          ${scoreSectionHtml}
        </div>

        <div class="gw-layer-desc-footer">
          <button id="gw-layer-desc-ok-btn" class="gw-layer-desc-ok-btn">Close</button>
        </div>
      </div>
    `;

    // Event listeners
    const closeBtn = document.getElementById('gw-layer-desc-close-btn');
    if (closeBtn) closeBtn.onclick = () => this.close();

    const okBtn = document.getElementById('gw-layer-desc-ok-btn');
    if (okBtn) okBtn.onclick = () => this.close();

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }
}

export default LayerDescriptionModal;
